import { QueryClient } from '@tanstack/react-query';

import { getPermissionsUrl } from '../../constants/service-endpoints';
import { get } from '../http.service';
import { getSilentToken } from '../tokenUtils';
import {
    clearSalt,
    decryptPermissions,
    deriveKey,
    encryptPermissions,
    getOrCreateSalt,
    isCacheEnvelopeValid,
} from './permissionsV2.crypto';
import { PermissionSetDtoV2 } from './permissionsV2.types';

const CACHE_KEY = (id: string) => `cop:permissionsV2:${id}`;
// Module-level: in-page deduplication (cleared on AEM page reload — expected)
export const permissionsQueryClient = new QueryClient();

// Module-level: derived key cached in memory for the lifetime of this page
let cachedKey: CryptoKey | null = null;

const getOrDeriveKey = async (): Promise<CryptoKey | null> => {
    if (cachedKey) return cachedKey;
    const token = getSilentToken();
    if (!token) return null;
    const salt = getOrCreateSalt();
    cachedKey  = await deriveKey(token, salt);
    return cachedKey;
}

export const fetchPermissionsV2 = (customerId: string): Promise<PermissionSetDtoV2> =>
    permissionsQueryClient.fetchQuery<PermissionSetDtoV2>({
        queryKey: ['PermissionsV2', customerId],
        staleTime: 5 * 60 * 1000,
        queryFn:   async () => {
            const storageKey = CACHE_KEY(customerId);
            const stored     = sessionStorage.getItem(storageKey);

            if (isCacheEnvelopeValid(stored)) {
                const key = await getOrDeriveKey();
                if (key && stored) {
                    const cached = await decryptPermissions(stored, key);
                    if (cached) return cached;
                    // decryption failed (tampered) — fall through to API
                }
            }

            // Cache miss or decryption failure → fetch from API
            const data = await get<PermissionSetDtoV2>(getPermissionsUrl(customerId))

            // Encrypt and persist for subsequent AEM page navigations
            const key = await getOrDeriveKey();
            if (key) {
                const encrypted = await encryptPermissions(data, key);
                sessionStorage.setItem(storageKey, encrypted);
            }

            return data;
        },
    })

export const clearPermissionsCache = (customerId: string): void => {
    sessionStorage.removeItem(CACHE_KEY(customerId))
    clearSalt()
    cachedKey = null
    permissionsQueryClient.removeQueries({ queryKey: ['PermissionsV2', customerId] })
}